import { instanceKey } from "@/lib/instanceKey";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { instanceHeaders } from "@/hooks/use-access";
import { notificationRulesKey } from "@/hooks/use-notifications";
import { apiRequest } from "@/lib/api";
import type { InstanceView } from "@shared/types/instances";
import type { InstanceSettingsBody, InstanceSettingsView } from "@shared/types/settings";

/**
 * The instance and its settings (FR-57, FR-72).
 *
 * The Teams webhook is a secret: it goes out as a VALUE exactly once, on the way
 * in, and comes back only as `{ vaultName, secretName }` (SEC-2). Nothing here
 * ever holds the URL after a save, which is why the screen's field reads "Stored"
 * with a Replace action rather than an input with a value in it.
 */

export const INSTANCE_QUERY_KEY = "/api/v2/instances/current";
export const INSTANCE_SETTINGS_QUERY_KEY = "/api/v2/instances/current/settings";

export function useInstance(enabled = true) {
  const { selectedInstance } = useTenantInstance();
  return useQuery<InstanceView>({
    queryKey: instanceKey(INSTANCE_QUERY_KEY, selectedInstance?.id),
    enabled,
    // See `instanceHeaders` in `use-access.ts` for why the header is explicit.
    queryFn: async ({ signal }) =>
      apiRequest<InstanceView>(INSTANCE_QUERY_KEY, {
        signal,
        headers: instanceHeaders(selectedInstance?.id),
      }),
  });
}

export function useInstanceSettings(enabled = true) {
  const { selectedInstance } = useTenantInstance();
  return useQuery<InstanceSettingsView>({
    queryKey: instanceKey(INSTANCE_SETTINGS_QUERY_KEY, selectedInstance?.id),
    enabled,
    queryFn: async ({ signal }) =>
      apiRequest<InstanceSettingsView>(INSTANCE_SETTINGS_QUERY_KEY, {
        signal,
        headers: instanceHeaders(selectedInstance?.id),
      }),
  });
}

export function useSaveInstanceSettings() {
  const client = useQueryClient();
  const { selectedInstance } = useTenantInstance();
  return useMutation({
    mutationFn: async (body: InstanceSettingsBody) =>
      apiRequest<InstanceSettingsView>(INSTANCE_SETTINGS_QUERY_KEY, {
        method: "PUT",
        body,
        // #1110: a save that carries the Teams webhook needs a recent sign-in.
        stepUp: "change",
        headers: instanceHeaders(selectedInstance?.id),
      }),
    onSuccess: (view, body) => {
      client.setQueryData(instanceKey(INSTANCE_SETTINGS_QUERY_KEY, selectedInstance?.id), view);
      // The notification rules view says whether a webhook is stored (`teamsConfigured`), and "Add a
      // rule" pre-fills Teams from it. A save that set or cleared the webhook makes a cached
      // rules view wrong, so it is read again (review B1).
      if (body.teamsWebhookUrl !== undefined) {
        void client.invalidateQueries({ queryKey: notificationRulesKey(selectedInstance?.id) });
      }
    },
  });
}
